import React, { useEffect, useRef, useState } from 'react';
import { Button, DynamicSelect, Modal, TitleBar, WidgetWrapper } from 'uxp/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

declare global {
  interface Window {
    mapsindoors: any;
    google: any;
    initMap: any;
  }
}

interface Location {
  id: string;
  properties: {
    name: string;
    anchor: {
      coordinates: [number, number];
    };
    floor: number;
  };
}

const MapsIndoorsComponent: React.FC<{ mapsIndoorsKey?: string, initialCenterLatitude?: string, initialCenterLongitude?: string, zoom?: string }> = ({ mapsIndoorsKey = "d876ff0e60bb430b8fabb145", initialCenterLatitude = '38.8974905', initialCenterLongitude = '-77.0362723', zoom = '17' }) => {
  const googleMapsInstanceRef = useRef<any>(null);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const floorSelectorRef = useRef<HTMLDivElement | null>(null);
  
  const [travelMode, setTravelMode] = useState("walking");

  const [fromVenue, setFromVenue] = useState<Location | null>(null);
  const [toVenue, setToValue] = useState<Location | null>(null);

  const [showRoute, setShowRoute] = useState(false);

  const getRoute = () => {
    setShowRoute(true);
    handleClose();
  }

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setShowRoute(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [searchLocation, setSearchLocation] = useState<Location | null>(null);

  useEffect(() => {
    if (searchLocation && googleMapsInstanceRef.current) {
      const zoomCoordinate = { lat: searchLocation.properties.anchor.coordinates[1], lng: searchLocation.properties.anchor.coordinates[0] };
      googleMapsInstanceRef.current.setCenter(zoomCoordinate);
      googleMapsInstanceRef.current.setZoom(21); // Choose the zoom level you prefer
    }
  }, [searchLocation]);

  const load = () => {
    if (window.mapsindoors) {
      if (mapRef.current && floorSelectorRef.current) {

        const initialCenterLatitudeX = parseFloat(initialCenterLatitude);
        const initialCenterLongitudeX = parseFloat(initialCenterLongitude);
        const zoomX = parseFloat(zoom);

        const mapViewOptions = {
          element: mapRef.current,
          center: { lat: initialCenterLatitudeX, lng: initialCenterLongitudeX }, // The White House
          zoom: zoomX,
          maxZoom: 22,

          args: {
            tilt: 20,
          }
        };
        const mapViewInstance = new window.mapsindoors.mapView.GoogleMapsView(mapViewOptions);
        const mapsIndoorsInstance = new window.mapsindoors.MapsIndoors({ mapView: mapViewInstance });
        const googleMapsInstance = mapViewInstance.getMap();
        googleMapsInstanceRef.current = googleMapsInstance;

        // const positionControlElement = document.createElement("div");
        // const positionControl = new window.mapsindoors.PositionControl(positionControlElement, {
        //   mapsIndoors: mapsIndoorsInstance,
        // });

        // positionControlElement.addEventListener('click', function () {
        //   navigator.geolocation.getCurrentPosition(function (position) {
        //     const fromLocation = {
        //       lat: position.coords.latitude,
        //       lng: position.coords.longitude
        //     };
        //     // Here, you can set the fromLocation as the value of your search 'from' field
        //   });
        // });
        // googleMapsInstance.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(positionControlElement);



        const externalDirectionsProvider = new window.mapsindoors.directions.GoogleMapsProvider();
        const miDirectionsServiceInstance = new window.mapsindoors.services.DirectionsService(externalDirectionsProvider);
        const directionsRendererOptions = { mapsIndoors: mapsIndoorsInstance }
        const miDirectionsRendererInstance = new window.mapsindoors.directions.DirectionsRenderer(directionsRendererOptions);

        // Enable Live Data
        // const liveDataManagerInstance = new window.mapsindoors.LiveDataManager(mapsIndoorsInstance);
        // liveDataManagerInstance.enableLiveData(window.mapsindoors.LiveDataManager.LiveDataDomainTypes.POSITION);
        //liveDataManagerInstance.enableLiveData(window.mapsindoors.LiveDataManager.LiveDataDomainTypes.AVAILABILITY);
        //liveDataManagerInstance.enableLiveData(window.mapsindoors.LiveDataManager.LiveDataDomainTypes.OCCUPANCY);
        //liveDataManagerInstance.enableLiveData(window.mapsindoors.LiveDataManager.LiveDataDomainTypes.TEMPERATURE);

        // Floor Selector
        new window.mapsindoors.FloorSelector(floorSelectorRef.current, mapsIndoorsInstance);
        googleMapsInstance.controls[window.google.maps.ControlPosition.RIGHT_TOP].push(floorSelectorRef.current);

        if (showRoute) {
          const originLocationCoordinate = { lat: fromVenue.properties.anchor.coordinates[1], lng: fromVenue.properties.anchor.coordinates[0], floor: fromVenue.properties.floor };
          const destinationCoordinate = { lat: toVenue.properties.anchor.coordinates[1], lng: toVenue.properties.anchor.coordinates[0], floor: toVenue.properties.floor };

          // Route parameters
          const routeParameters = {
            origin: originLocationCoordinate,
            destination: destinationCoordinate,
            travelMode: travelMode.toUpperCase()
          };

          // Get route from directions service
          miDirectionsServiceInstance.getRoute(routeParameters).then((directionsResult: any) => {
            // Use directions render to display route
            miDirectionsRendererInstance.setRoute(directionsResult);
          });
        }
      }
    }
  }

  useEffect(() => {
    const defaultMapsIndoorsKey = 'd876ff0e60bb430b8fabb145';

    let key = mapsIndoorsKey || defaultMapsIndoorsKey;
    key = key === '' ? defaultMapsIndoorsKey : key;

    const loadScript = (src: string, id: string) => {
      return new Promise<void>((resolve, reject) => {

        if (id == 'mapsgoogleapis') {
          if (document.getElementById(id)) {
            resolve();
            return;
          }

          const script = document.createElement('script');
          script.src = src;
          script.id = id;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = reject;
          document.body.appendChild(script);

        }
        else {
          const existingScript = document.getElementById(id);

          if (existingScript) {
            existingScript.remove();
          }

          const script = document.createElement('script');
          script.src = src;
          script.id = id;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = reject;
          document.body.appendChild(script);
        }

      });
    };


    window.initMap = function () {
      // JS API is loaded and available
    };

    Promise.all([
      loadScript(`https://app.mapsindoors.com/mapsindoors/js/sdk/4.21.4/mapsindoors-4.21.4.js.gz?apikey=${key}`, 'appmapsindoors'),
      loadScript('https://maps.googleapis.com/maps/api/js?libraries=geometry&key=AIzaSyDXfCksxSFbzFosXEaUsFpBS7FM8IMRoxo&callback=initMap', 'mapsgoogleapis')
    ]).then(() => {
      load();
    });
  }, []);

  useEffect(() => {
    load();
  }, [ travelMode, showRoute]);

  async function getOptions(max: number, pageToken: string, args: any): Promise<{ items: any[]; pageToken: string; }> {
    let value = args?.query ?? "";

    const newLocations = await window.mapsindoors.services.LocationsService.getLocations({ q: value });

    const amendedLocations = newLocations.map((location: any) => ({
      ...location,
      name: location.properties?.name,
    }));

    return { items: amendedLocations, pageToken };
  }

  return (
    <WidgetWrapper>
      <TitleBar title='MapsIndoors'>
        <div style={{ display: 'flex', alignItems: 'center' }}>
 
          <DynamicSelect
            selected={searchLocation?.properties?.name ?? ""}
            options={(max: number, pageToken: string, args?: any) => getOptions(max, pageToken, args)}
            onChange={(value) => { setSearchLocation(value) }}
            placeholder="search"
            labelField="name"
            timeout={500}
          />

          <button style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer' }} onClick={handleOpen}>
            <FontAwesomeIcon icon="directions" />
          </button>

        </div>

        <Modal show={open} onClose={handleClose} headerContent={<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px'}}>
          <span>Get Directions</span>
        </div>
        }>

          <div style={{ padding: '16px' }}>
            <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <button style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer' }} onClick={() => setTravelMode('walking')}>
                  <FontAwesomeIcon icon="walking" style={{ color: travelMode === 'walking' ? 'blue' : undefined }} />

                </button>
                <button style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer' }} onClick={() => setTravelMode('driving')}>
                  <FontAwesomeIcon icon="car" style={{ color: travelMode === 'driving' ? 'blue' : undefined }} />
                </button>
                <button style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer' }} onClick={() => setTravelMode('bicycling')}>
                  <FontAwesomeIcon icon="bicycle" style={{ color: travelMode === 'bicycling' ? 'blue' : undefined }} />
                </button>
                <button style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer' }} onClick={() => setTravelMode('transit')}>
                  <FontAwesomeIcon icon="bus" style={{ color: travelMode === 'transit' ? 'blue' : undefined }} />
                </button>
                

              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
               
                <DynamicSelect
                  selected={fromVenue?.properties?.name ?? ""}
                  options={(max: number, pageToken: string, args?: any) => getOptions(max, pageToken, args)}
                  onChange={(value) => { setFromVenue(value) }}
                  placeholder="from"
                  labelField="name"
                  timeout={500}
                />

                <DynamicSelect
                  selected={toVenue?.properties?.name ?? ""}
                  options={(max: number, pageToken: string, args?: any) => getOptions(max, pageToken, args)}
                  onChange={(value) => { setToValue(value) }}
                  placeholder="to"
                  labelField="name"
                  timeout={500}
                />
               
                <Button onClick={() => getRoute()} title={' Show Route'} >

                </Button>

              </div>
            </div>

          </div>
        </Modal>
      </TitleBar>
      <div style={{ position: 'relative', width: '100%', height: '500px' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }}></div>
        <div ref={floorSelectorRef}></div>
      </div>
    </WidgetWrapper>
  );


};

export default MapsIndoorsComponent;

